import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Refresh, PictureAsPdf, TableChart, Assessment } from '@mui/icons-material';
import {
    PERIODS,
    periodRange,
    fetchPaymentsInRange,
    buildCashReport,
    money,
    STATUS_LABELS,
    downloadCsv,
    buildReportHtml,
    printHtml,
} from '../../utils/cashReport';

const toInputDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const STATUS_TONES = {
    paid: 'bg-emerald-50 text-emerald-700', cashed: 'bg-emerald-50 text-emerald-700',
    pending: 'bg-amber-50 text-amber-700', failed: 'bg-red-50 text-red-700', expired: 'bg-slate-100 text-slate-600',
};

const Panel = ({ title, children, className = '' }) => (
    <div className={`rounded-2xl bg-white border border-slate-200 p-5 ${className}`}>
        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">{title}</p>
        {children}
    </div>
);

/** Liste « libellé / nombre / montant » avec barre proportionnelle. */
const Breakdown = ({ rows, currency }) => {
    const max = Math.max(1, ...rows.map((r) => r.totals[currency] || 0));
    if (!rows.length) return <p className="text-sm text-slate-500">Aucun paiement réussi sur la période.</p>;
    return (
        <ul className="space-y-3">
            {rows.map((r) => (
                <li key={r.key}>
                    <div className="flex items-center justify-between gap-3 text-sm">
                        <span className="font-medium text-slate-800 truncate">{r.label}</span>
                        <span className="text-slate-500 shrink-0">{r.count} · <b className="text-slate-900">{money(r.totals[currency] || 0, currency)}</b></span>
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500" style={{ width: `${((r.totals[currency] || 0) / max) * 100}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
};

/**
 * Rapport de caisse sur les encaissements réels (pos-service).
 * - Caissier : `cashierId` fixe (ses propres paiements).
 * - Admin : `terminals` permet de choisir une caisse (terminal + caissier) ou toutes.
 */
const CashReport = ({ organization, cashierId = null, terminals = [], cashierNameById = {}, scopeLabel: fixedScopeLabel }) => {
    const today = toInputDate(new Date());
    const [period, setPeriod] = useState('today');
    const [customFrom, setCustomFrom] = useState(today);
    const [customTo, setCustomTo] = useState(today);
    const [terminalId, setTerminalId] = useState('all');
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currency, setCurrency] = useState('CDF');
    const [showAll, setShowAll] = useState(false);

    const range = useMemo(() => periodRange(period, customFrom, customTo), [period, customFrom, customTo]);
    const periodLabel = useMemo(() => {
        const f = range.from.toLocaleDateString('fr-FR');
        const t = range.to.toLocaleDateString('fr-FR');
        return f === t ? `Journée du ${f}` : `Du ${f} au ${t}`;
    }, [range]);

    const load = useCallback(async () => {
        if (!organization?.id) return;
        setLoading(true);
        setError(null);
        try {
            setPayments(await fetchPaymentsInRange(organization.id, range.from, range.to));
        } catch (e) {
            setError(e?.response?.data?.message || e.message || 'Impossible de charger les paiements');
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [organization?.id, range]);

    useEffect(() => { load(); }, [load]);

    const selectedTerminal = terminals.find((t) => t.id === terminalId);
    const scoped = useMemo(() => payments.filter((p) => {
        if (cashierId) return p.cashierId === cashierId;
        if (!selectedTerminal) return true;
        return p.terminalId === selectedTerminal.id || (selectedTerminal.managerId && p.cashierId === selectedTerminal.managerId);
    }), [payments, cashierId, selectedTerminal]);

    const report = useMemo(() => buildCashReport(scoped, { cashierNameById }), [scoped, cashierNameById]);
    // CDF et USD toujours proposés, plus toute autre devise présente dans les paiements
    const currencies = useMemo(
        () => Array.from(new Set(['CDF', 'USD', ...Object.keys(report.byCurrency)])),
        [report]
    );

    const scopeLabel = fixedScopeLabel
        || (selectedTerminal ? `${selectedTerminal.name}${selectedTerminal.cashierName ? ` · ${selectedTerminal.cashierName}` : ''}` : 'Toutes les caisses');
    const maxHour = Math.max(1, ...report.byHour.map((h) => h.totals[currency] || 0));
    const paymentsInCurrency = useMemo(() => scoped.filter((p) => p.currency === currency), [scoped, currency]);
    const visiblePayments = showAll ? paymentsInCurrency : paymentsInCurrency.slice(0, 50);
    const fileBase = `rapport-caisse-${toInputDate(range.from)}${period !== 'today' && period !== 'yesterday' ? `_${toInputDate(range.to)}` : ''}`;

    const chip = (active) => `px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${
        active ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
    }`;

    return (
        <div className="space-y-5">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center shadow">
                        <Assessment className="text-white" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">Rapport de caisse</h1>
                        <p className="text-sm text-slate-500">{periodLabel} · {scopeLabel}</p>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={load} disabled={loading}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        <Refresh fontSize="small" className={loading ? 'animate-spin' : ''} /> Actualiser
                    </button>
                    <button type="button" disabled={loading}
                        onClick={() => downloadCsv(scoped, `${fileBase}.csv`, cashierNameById)}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                        <TableChart fontSize="small" /> Excel (CSV)
                    </button>
                    <button type="button" disabled={loading}
                        onClick={() => printHtml(buildReportHtml({ report, payments: scoped, organization, periodLabel, scopeLabel, cashierNameById }))}
                        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-sm font-semibold text-white hover:from-amber-600 hover:to-orange-600 disabled:opacity-50">
                        <PictureAsPdf fontSize="small" /> Exporter en PDF
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-2xl p-4 border border-slate-200 flex flex-wrap items-center gap-2">
                {PERIODS.map((p) => (
                    <button type="button" key={p.id} onClick={() => setPeriod(p.id)} className={chip(period === p.id)}>{p.label}</button>
                ))}
                {/* Devise du rapport */}
                <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1" role="group" aria-label="Devise">
                    {currencies.map((c) => (
                        <button type="button" key={c} onClick={() => setCurrency(c)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${currency === c ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}>
                            {c}
                        </button>
                    ))}
                </div>
                {period === 'custom' && (
                    <div className="flex items-center gap-2">
                        <input type="date" value={customFrom} max={customTo} onChange={(e) => setCustomFrom(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
                        <span className="text-slate-400">→</span>
                        <input type="date" value={customTo} min={customFrom} max={today} onChange={(e) => setCustomTo(e.target.value)}
                            className="px-3 py-2 border border-slate-200 rounded-xl text-sm" />
                    </div>
                )}
                {!cashierId && terminals.length > 0 && (
                    <select value={terminalId} onChange={(e) => setTerminalId(e.target.value)} aria-label="Caisse"
                        className="ml-auto px-3 py-2.5 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 bg-white">
                        <option value="all">Toutes les caisses</option>
                        {terminals.map((t) => (
                            <option key={t.id} value={t.id}>{t.name}{t.cashierName ? ` · ${t.cashierName}` : ''}</option>
                        ))}
                    </select>
                )}
            </div>

            {error && <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {['CDF', 'USD'].map((c) => (
                    <div key={c} className={`rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-4 text-white ${currency === c ? 'ring-2 ring-amber-400' : ''}`}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-amber-300">Encaissé {c}</p>
                        <p className="mt-1 text-2xl font-bold tabular-nums">{loading ? '…' : money(report.byCurrency[c]?.collected || 0, c)}</p>
                        <p className="text-xs text-slate-300">Ticket moyen : {money(report.byCurrency[c]?.average || 0, c)}</p>
                    </div>
                ))}
                <div className="rounded-2xl bg-white border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Paiements réussis</p>
                    <p className="mt-1 text-2xl font-bold text-emerald-600">{loading ? '…' : report.statusCounts.success}</p>
                    <p className="text-xs text-slate-500">sur {report.total} tentative(s)</p>
                </div>
                <div className="rounded-2xl bg-white border border-slate-200 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Taux de réussite</p>
                    <p className="mt-1 text-2xl font-bold text-slate-900">{loading ? '…' : `${report.successRate} %`}</p>
                    <p className="text-xs text-slate-500">
                        {report.statusCounts.pending} en attente · {report.statusCounts.failed} échoué(s) · {report.statusCounts.expired} expiré(s)
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Panel title="Par canal"><Breakdown rows={report.byChannel} currency={currency} /></Panel>
                <Panel title="Par opérateur"><Breakdown rows={report.byOperator} currency={currency} /></Panel>
                <Panel title={`Encaissements par heure (${currency})`} className="lg:col-span-2">
                    <div className="flex items-end gap-1 h-32">
                        {report.byHour.map((h) => (
                            <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full" title={`${h.hour}h : ${h.count} paiement(s), ${money(h.totals[currency] || 0, currency)}`}>
                                <div className="w-full rounded-t bg-gradient-to-t from-amber-500 to-orange-400" style={{ height: `${((h.totals[currency] || 0) / maxHour) * 100}%`, minHeight: h.count ? 3 : 0 }} />
                                <span className="mt-1 text-[10px] text-slate-400">{h.hour % 3 === 0 ? `${h.hour}h` : ''}</span>
                            </div>
                        ))}
                    </div>
                </Panel>
                {!cashierId && terminalId === 'all' && (
                    <Panel title="Par caissier" className="lg:col-span-2"><Breakdown rows={report.byCashier} currency={currency} /></Panel>
                )}
            </div>

            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <p className="font-semibold text-slate-800">Détail des paiements en {currency} <span className="text-slate-400 font-normal">({paymentsInCurrency.length})</span></p>
                </div>
                {paymentsInCurrency.length === 0 ? (
                    <p className="p-10 text-center text-sm text-slate-500">{loading ? 'Chargement…' : 'Aucun paiement sur cette période.'}</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[720px] text-sm">
                            <thead>
                                <tr className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                                    <th className="px-5 py-3">Date</th>
                                    <th className="px-5 py-3">Référence</th>
                                    <th className="px-5 py-3">Client</th>
                                    <th className="px-5 py-3">Canal · Opérateur</th>
                                    <th className="px-5 py-3">Statut</th>
                                    <th className="px-5 py-3 text-right">Montant</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {visiblePayments.map((p) => {
                                    const at = p.paidAt || p.createdAt;
                                    return (
                                        <tr key={p.id}>
                                            <td className="px-5 py-3 text-slate-600 whitespace-nowrap">{at?.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                                            <td className="px-5 py-3 font-mono text-slate-800">{p.reference}</td>
                                            <td className="px-5 py-3 text-slate-800">{p.payerName}</td>
                                            <td className="px-5 py-3 text-slate-600">{p.channel}{p.operator ? ` · ${p.operator}` : ''}</td>
                                            <td className="px-5 py-3">
                                                <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${STATUS_TONES[p.status] || 'bg-slate-100 text-slate-600'}`}>{STATUS_LABELS[p.status] || p.status}</span>
                                            </td>
                                            <td className="px-5 py-3 text-right font-semibold text-slate-900 tabular-nums">{money(p.amount, p.currency)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {paymentsInCurrency.length > 50 && !showAll && (
                            <button type="button" onClick={() => setShowAll(true)} className="w-full py-3 text-sm font-medium text-amber-700 hover:bg-amber-50">
                                Afficher les {paymentsInCurrency.length - 50} autres paiements
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CashReport;
